// Data caching utility for faster app loading
// Stores data in localStorage and updates from backend in background

const CACHE_KEYS = {
  ARTICLES: 'smartreadgo_articles_cache',
  CATEGORIES: 'smartreadgo_categories_cache',
  PUBLISHERS: 'smartreadgo_publishers_cache',
  CACHE_TIMESTAMP: 'smartreadgo_cache_timestamp',
};

// Cache expiry time (5 minutes) - data will still show from cache but refresh in background
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Get cached data from localStorage
 */
export const getCachedData = (key) => {
  try {
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Error reading from cache:', error);
  }
  return null;
};

/**
 * Save data to localStorage cache
 */
export const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
  } catch (error) {
    console.error('Error writing to cache:', error);
    // If storage is full, clear old cache
    if (error.name === 'QuotaExceededError') {
      clearAllCache();
    }
  }
};

/**
 * Check if cache is stale (older than CACHE_EXPIRY_MS)
 */
export const isCacheStale = () => {
  try {
    const timestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
    if (!timestamp) return true;
    return Date.now() - parseInt(timestamp) > CACHE_EXPIRY_MS;
  } catch (error) {
    return true;
  }
};

/**
 * Get cached articles with optional filter key
 */
export const getCachedArticles = (filterKey = 'all_all') => {
  const cache = getCachedData(CACHE_KEYS.ARTICLES);
  if (cache && cache[filterKey]) {
    return cache[filterKey];
  }
  return null;
};

/**
 * Save articles to cache with filter key
 */
export const setCachedArticles = (filterKey, articles) => {
  const cache = getCachedData(CACHE_KEYS.ARTICLES) || {};
  cache[filterKey] = articles;
  setCachedData(CACHE_KEYS.ARTICLES, cache);
};

/**
 * Get cached categories
 */
export const getCachedCategories = () => {
  return getCachedData(CACHE_KEYS.CATEGORIES);
};

/**
 * Save categories to cache
 */
export const setCachedCategories = (categories) => {
  setCachedData(CACHE_KEYS.CATEGORIES, categories);
};

/**
 * Get cached publishers
 */
export const getCachedPublishers = () => {
  return getCachedData(CACHE_KEYS.PUBLISHERS);
};

/**
 * Save publishers to cache
 */
export const setCachedPublishers = (publishers) => {
  setCachedData(CACHE_KEYS.PUBLISHERS, publishers);
};

/**
 * Clear all cached data
 */
export const clearAllCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Generate a filter key for article caching
 */
export const generateFilterKey = (category, publisher) => {
  return `${category || 'all'}_${publisher || 'all'}`;
};
